import React, { useState, useRef, useMemo, KeyboardEvent } from "react";
import {
  Box,
  TextField,
  Avatar,
  Typography,
  styled,
  IconButton,
  Button,
  DialogContent,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsByBugIdQuery,
  useUpdateCommentMutation,
} from "@/features/bughurd/api";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import { getBackgroundColor } from "@/pages/Board/utils";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ModalBasic from "@/Components/Common/ModalBasic";
import { useAppSelector } from "@/app/hooks";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import MicOffIcon from "@mui/icons-material/MicOff";
import storageService from "@/Components/helpers/storageService";
import axios from "axios";
import { useGetAiCompanyConfigQuery } from "@/features/AiApi/chatApi";
import AiNotConfiguredDialog from "@/Components/Common/AiNotConfiguredDialog";

const CommentInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    "&.Mui-focused": {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

const MentionedText = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: "rgba(99, 102, 241, 0.1)",
  padding: "2px 8px",
  borderRadius: "6px",
  fontWeight: 600,
  fontSize: "13px",
}));

const renderCommentText = (
  text: string,
  users: Array<{ _id: string; fullName: string }>,
) => {
  let parts: (string | JSX.Element)[] = [text];

  users.forEach((user) => {
    const mention = `@${user.fullName}`;
    parts = parts
      ?.map((part) => {
        if (typeof part !== "string") return part;

        const splitText = part?.split(new RegExp(mention, "i"));

        if (splitText?.length === 1) return part;

        const matches = part.match(new RegExp(mention, "ig")) || [];

        return splitText.map((text, index) => {
          if (index === splitText.length - 1) return text;
          return (
            <>
              {text}
              <MentionedText key={`mention-${user._id}-${index}`}>
                {matches[index] || mention}
              </MentionedText>
            </>
          );
        });
      })
      .flat();
  });

  return parts;
};

export default function Comments({ users, taskId, bugDetailData }) {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const { data: aiConfigData } = useGetAiCompanyConfigQuery({});
  const isAiConfigured = useMemo(() => {
    const providers = aiConfigData?.data?.apiProviders || [];
    return providers?.some(
      (p: any) => p?.isActive && p?.apiKey?.trim()
    );
  }, [aiConfigData]);

  const [showAiConfigDialog, setShowAiConfigDialog] = useState(false);

  const { currentUser } = useAppSelector((state) => state.appGlobal);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedUserIndex, setSelectedUserIndex] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [mentionSearch, setMentionSearch] = useState("");
  const [showMentionList, setShowMentionList] = useState(false);
  // const [mentionPosition, setMentionPosition] = useState({
  //   left: 0,
  //   top: 0,
  // });

  const [isDeleteCommentModal, setIsDeleteCommentModal] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [editableCommentId, setEditableCommentId] = useState<string | null>(
    null,
  );
  const [editedComment, setEditedComment] = useState<string>("");
  const [showEditMentionList, setShowEditMentionList] = useState(false);

  // Voice recording and AI improve states
  const [isRecording, setIsRecording] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isProcessingSpeech, setIsProcessingSpeech] = useState(false);
  const recognitionRef = useRef<any>(null);
  const isRecordingRef = useRef(false);
  const textBeforeRecordingRef = useRef("");
  const processingTimeoutRef = useRef<any>(null);

  const bugId = taskId;
  const { data: comments, refetch: refetchComments } =
    useGetCommentsByBugIdQuery(
      { bugId },
      {
        skip: !bugId,
        refetchOnMountOrArgChange: true,
      },
    );

  const commentsData = Array.isArray(comments?.data?.decryptedData?.items)
    ? comments.data.decryptedData.items.filter(
      (comment) => !comment.videoTimestamp,
    )
    : [];

  const [createComment, { isLoading: isCreating }] = useCreateCommentMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();
  const [updateComment, { isLoading: isUpdating }] = useUpdateCommentMutation();

  // Initialize speech recognition
  React.useEffect(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event: any) => {
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + " ";
        }
      }

      if (final) {
        // Clear any existing timeout
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }

        // Show processing state briefly
        setIsProcessingSpeech(true);
        setProcessingMessage("Processing speech...");

        // Update text immediately
        setComment((prev) => prev + final);

        // Show processing message very briefly (200ms) for visual feedback
        processingTimeoutRef.current = setTimeout(() => {
          setIsProcessingSpeech(false);
          // Only set back to "Listening..." if still recording
          if (isRecordingRef.current) {
            setProcessingMessage("Listening...");
          } else {
            setProcessingMessage("");
          }
        }, 200);
      }
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setIsProcessingSpeech(false);
      setProcessingMessage("");
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (event.error !== "no-speech" && event.error !== "aborted") {
        setIsRecording(false);
        toast.error(t("Speech recognition error"));
      }
    };

    recognition.onend = () => {
      // Only restart if we're still supposed to be recording
      if (isRecordingRef.current) {
        try {
          recognition.start();
          // Keep showing "Listening..." if restarting
          setProcessingMessage("Listening...");
        } catch (e) {
          console.error("Error restarting recognition:", e);
          setIsRecording(false);
          setIsProcessingSpeech(false);
          setProcessingMessage("");
        }
      }
      // If not recording, states will be cleared by stopRecording
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.error("Error aborting recognition:", e);
        }
      }
    };
  }, []);

  React.useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setComment(value);

    // Check for @ mentions anywhere in the text
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtPos = textBeforeCursor.lastIndexOf("@");

    if (lastAtPos >= 0) {
      // Check if the @ is part of a word (not preceded by a space or at start)
      const isMention =
        lastAtPos === 0 ||
        textBeforeCursor[lastAtPos - 1] === " " ||
        textBeforeCursor[lastAtPos - 1] === "\n";

      if (isMention) {
        const mentionText = textBeforeCursor.substring(lastAtPos + 1);
        setMentionSearch(mentionText);
        setShowMentionList(true);
        setSelectedUserIndex(0);
        return;
      }
    }

    setShowMentionList(false);
  };

  const handleEditedCommentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setEditedComment(value);

    // Check for @ mentions anywhere in the text
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtPos = textBeforeCursor.lastIndexOf("@");

    if (lastAtPos >= 0) {
      // Check if the @ is part of a word (not preceded by a space or at start)
      const isMention =
        lastAtPos === 0 ||
        textBeforeCursor[lastAtPos - 1] === " " ||
        textBeforeCursor[lastAtPos - 1] === "\n";

      if (isMention) {
        const mentionText = textBeforeCursor.substring(lastAtPos + 1);
        setMentionSearch(mentionText);
        setShowEditMentionList(true);
        setSelectedUserIndex(0);
        return;
      }
    }

    setShowEditMentionList(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const mentionListVisible = editableCommentId
      ? showEditMentionList
      : showMentionList;

    if (!mentionListVisible || filteredUsers.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedUserIndex((prev) =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedUserIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (
          selectedUserIndex >= 0 &&
          selectedUserIndex < filteredUsers.length
        ) {
          handleMentionSelect(filteredUsers[selectedUserIndex]);
        }
        break;
      case "Escape":
        editableCommentId
          ? setShowEditMentionList(false)
          : setShowMentionList(false);
        break;
    }
  };

  const handleDelete = async () => {
    try {
      await deleteComment(deleteCommentId).unwrap();
      toast.success(t("Comment deleted successfully"));
      setIsDeleteCommentModal(false);
      refetchComments(); // Refetch comments immediately
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleMentionSelect = (user: (typeof users)[0]) => {
    const targetComment = editableCommentId ? editedComment : comment;
    const inputElement = inputRef.current;

    if (!inputElement) return;

    const cursorPosition = inputElement.selectionStart || 0;
    const textBeforeCursor = targetComment.substring(0, cursorPosition);
    const lastAtPos = textBeforeCursor.lastIndexOf("@");

    if (lastAtPos >= 0) {
      const textBeforeAt = targetComment.substring(0, lastAtPos);
      const textAfterAt = targetComment.substring(cursorPosition);
      const updatedComment = `${textBeforeAt}@${user.fullName} ${textAfterAt}`;

      if (editableCommentId) {
        setEditedComment(updatedComment);
        setShowEditMentionList(false);
      } else {
        setComment(updatedComment);
        setShowMentionList(false);
      }

      setTimeout(() => {
        const newCursorPos = lastAtPos + user.fullName.length + 2;
        inputElement.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.fullName.toLowerCase().includes(mentionSearch.toLowerCase()),
  );

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    const payload = {
      comment: comment,
      itemId: taskId,
      module: "BUG_TRACKING",
      type: "PUBLIC",
      isComment: true,
      mentionedUsers: [],
      expiredAt: "",
      parentComment: "",
      pageUrl: "",
      coordinates: [],
      color: "",
      attachments: [],
      jiraObj: {
        itemkey: bugDetailData?.data?.decryptedData?.jiraConfig?.itemKey,
      },
    };

    try {
      const res = await createComment(payload).unwrap();
      if (
        res?.status === "200" ||
        res?.data?.decryptedData?._id ||
        res?.data?.decryptedData?.syncStatus === "pending_webhook"
      ) {
        toast.success(t("Comment added successfully"));
        setComment("");
        refetchComments();
      } else {
        toast.error(t("Failed to add comment"));
      }
    } catch (error) {
      console.error("Error creating comment:", error);
      toast.error(t("An error occurred while adding the comment"));
    }
  };

  const handleEditComment = (id: string, comment: string) => {
    setEditableCommentId(id);
    setEditedComment(comment);
  };

  const handleSaveEditedComment = async (commentId: string) => {
    const payload = {
      comment: editedComment,
      itemId: taskId,
      commentId: commentId,
    };

    try {
      await updateComment(payload).unwrap();
      setEditableCommentId(null);
      setEditedComment("");
      toast.success(t("Comment updated successfully!"));
      refetchComments(); // Refetch comments immediately
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error(t("Failed to update comment"));
    }
  };

  // Voice recording functions
  const startRecording = () => {
    if (!recognitionRef.current) return;

    try {
      textBeforeRecordingRef.current = comment;

      // Clear any previous timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }

      recognitionRef.current.start();
      setIsRecording(true);
      setIsProcessingSpeech(false);
      setProcessingMessage("Listening...");
    } catch (error) {
      console.error("Error starting recognition:", error);
      setProcessingMessage("");
      toast.error(t("Failed to start recording"));
    }
  };

  const stopRecording = () => {
    if (!recognitionRef.current) return;

    try {
      // First stop the recognition
      recognitionRef.current.stop();

      // Update states
      setIsRecording(false);

      // Show finalizing message briefly
      setIsProcessingSpeech(true);
      setProcessingMessage("Finalizing...");

      // Clear finalizing message quickly (300ms)
      const finalizingTimeout = setTimeout(() => {
        setProcessingMessage("");
        setIsProcessingSpeech(false);
      }, 300);

      // Cleanup function
      return () => clearTimeout(finalizingTimeout);
    } catch (error) {
      console.error("Error stopping recognition:", error);
      setProcessingMessage("");
      setIsProcessingSpeech(false);
    }
  };

  const cancelRecording = () => {
    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.stop();
      setIsRecording(false);
      setComment(textBeforeRecordingRef.current);
      setProcessingMessage("");
      setIsProcessingSpeech(false);

      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    } catch (error) {
      console.error("Error canceling recognition:", error);
    }
  };

  // AI Improve function
  const handleAIImprove = async () => {
    if (!comment.trim()) return;

    if (!isAiConfigured) {
      setShowAiConfigDialog(true);
      return;
    }

    try {
      setIsAIProcessing(true);
      setProcessingMessage("AI is improving your comment...");
      const response = await axios.post(
        `${import.meta.env.VITE_CHAT_API_URL}/api/agents/task-refiner/generate-text`,
        {
          prompt: `Improve the comment: "${comment.trim()}"`,
          store: false,
          module: "general",
          businessApplication:
            storageService.get("businessApplication") || "123",
          team: storageService.get("team") || "111",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      if (response?.data?.text) {
        setComment(response.data.text);
        toast.success(t("Comment improved successfully"));
      } else {
        toast.error(t("No improvement suggestions available"));
      }
    } catch (error) {
      console.error("Error improving comment with AI:", error);
      toast.error(t("Failed to improve comment"));
    } finally {
      setIsAIProcessing(false);
      setProcessingMessage("");
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        p: 2,
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: theme.palette.text.primary,
            letterSpacing: "0.2px",
          }}
        >
          {t("Comments")} ({commentsData?.length || 0})
        </Typography>
      </Box>

      {/* Add Comment Section */}
      <Box
        mb={2}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: "8px",
          p: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.2s",
          "&:focus-within": {
            borderColor: theme.palette.divider,
            boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.05)",
          },
        }}
      >
        <Box>
          <Box sx={{ position: "relative" }}>
            <Box sx={{ position: "relative" }}>
              <CommentInput
                fullWidth
                multiline
                minRows={1}
                maxRows={5}
                placeholder={t("Add a comment... (Type @ to mention)")}
                value={comment}
                onChange={handleCommentChange}
                onKeyDown={handleKeyDown}
                inputRef={inputRef}
                disabled={isRecording || isAIProcessing || isProcessingSpeech}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: "transparent",
                    padding: "8px 12px",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc",
                    },
                    "&.Mui-focused": {
                      backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#fafbfc",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 !important",
                  },
                }}
              />

              {/* Processing Indicator */}
              {processingMessage && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    backgroundColor: theme.palette.mode === "dark" ? "rgba(30, 30, 30, 0.98)" : "rgba(255, 255, 255, 0.98)",
                    padding: "6px 12px",
                    borderRadius: "16px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                    zIndex: 10,
                  }}
                >
                  <CircularProgress size={14} thickness={4} />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {processingMessage}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Action Buttons Below Input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* Left side - Action buttons */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {/* Voice Recording Buttons */}
                {isRecording ? (
                  <>
                    {/* Cancel Recording */}
                    <IconButton
                      onClick={cancelRecording}
                      size="small"
                      sx={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: (theme) => theme.palette.grey[400],
                        color: "white",
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.grey[500],
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    {/* Stop Recording */}
                    <IconButton
                      onClick={stopRecording}
                      size="small"
                      sx={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: (theme) => theme.palette.grey[600],
                        color: "white",
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.grey[700],
                        },
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </>
                ) : (
                  <>
                    {/* Voice Recording Button */}
                    {isSupported ? (
                      <IconButton
                        onClick={startRecording}
                        size="small"
                        sx={{
                          width: "28px",
                          height: "28px",
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.secondary,
                          },
                        }}
                      >
                        <MicIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        disabled
                        sx={{ width: "28px", height: "28px" }}
                      >
                        <MicOffIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}

                    {/* AI Improve Button */}
                    <IconButton
                      onClick={handleAIImprove}
                      disabled={!comment.trim() || isAIProcessing || isRecording}
                      size="small"
                      sx={{
                        width: "28px",
                        height: "28px",
                        color: isAIProcessing ? "primary.main" : theme.palette.text.secondary,
                        "&:hover": {
                          backgroundColor: "rgba(99, 102, 241, 0.08)",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: theme.palette.action.disabled,
                        },
                      }}
                    >
                      {isAIProcessing ? (
                        <CircularProgress
                          size={14}
                          thickness={4}
                          sx={{ color: "primary.main" }}
                        />
                      ) : (
                        <AutoFixHighIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </>
                )}
              </Box>

              {/* Right side - Send button */}
              <IconButton
                size="small"
                disabled={!comment.trim() || isCreating || isRecording || isAIProcessing || isProcessingSpeech}
                onClick={handleAddComment}
                sx={{
                  width: "28px",
                  height: "28px",
                  color: comment.trim() && !isCreating ? "primary.main" : theme.palette.action.disabled,
                  "&:hover": {
                    backgroundColor: comment.trim()
                      ? "rgba(99, 102, 241, 0.08)"
                      : "transparent",
                  },
                  "&.Mui-disabled": {
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {isCreating ? (
                  <CircularProgress
                    size={14}
                    thickness={4}
                    sx={{ color: "primary.main" }}
                  />
                ) : (
                  <SendIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>

            {/* Mention Dropdown */}
            {showMentionList && filteredUsers.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  mt: 0.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  maxHeight: 200,
                  width: "280px",
                  overflow: "auto",
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
                  zIndex: 1000,
                }}
              >
                {filteredUsers.map((user, index) => (
                  <Box
                    key={user._id}
                    sx={{
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      bgcolor:
                        selectedUserIndex === index
                          ? "rgba(99, 102, 241, 0.12)"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "rgba(99, 102, 241, 0.12)",
                      },
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      transition: "all 0.15s",
                      borderBottom:
                        index < filteredUsers.length - 1
                          ? `1px solid ${theme.palette.divider}`
                          : "none",
                    }}
                    onClick={() => handleMentionSelect(user)}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    >
                      {user.fullName[0]}
                    </Avatar>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {user.fullName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Comments List */}
      <Box sx={{ mt: 2 }}>
        {commentsData && commentsData.length > 0 ? (
          commentsData.map((comment, index) => (
            <Box
              key={comment._id}
              sx={{
                mb: 1.5,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  p: 2,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.divider}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: theme.palette.divider,
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)",
                    "& .comment-actions": {
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Avatar */}
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: getBackgroundColor(
                      comment?.createdBy?._id || comment?.userName || "",
                    ),
                    fontSize: "13px",
                    fontWeight: 600,
                    flexShrink: 0,
                    border: `2px solid ${theme.palette.divider}`,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {(
                    comment?.createdBy?.fullName ||
                    comment?.userName ||
                    ""
                  )?.charAt(0).toUpperCase()}
                </Avatar>

                {/* Comment Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {comment?.createdBy?.fullName ||
                          comment?.userName ||
                          ""}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: theme.palette.text.secondary,
                          fontWeight: 400,
                        }}
                      >
                        {moment(comment.createdAt).fromNow()}
                      </Typography>
                    </Box>

                    {/* Action Buttons */}
                    {comment?.createdBy?._id === currentUser?._id && (
                      <Box
                        className="comment-actions"
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                        }}
                      >
                        <IconButton
                          onClick={() =>
                            handleEditComment(comment?._id, comment?.comment)
                          }
                          size="small"
                          sx={{
                            width: "26px",
                            height: "26px",
                            color: theme.palette.text.secondary,
                            padding: 0,
                            "&:hover": {
                              bgcolor: "rgba(99, 102, 241, 0.1)",
                              color: "primary.main",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setDeleteCommentId(comment?._id);
                            setIsDeleteCommentModal(true);
                          }}
                          size="small"
                          sx={{
                            width: "26px",
                            height: "26px",
                            color: theme.palette.text.secondary,
                            padding: 0,
                            "&:hover": {
                              bgcolor: "rgba(239, 68, 68, 0.1)",
                              color: "#dc2626",
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {/* Comment Text or Edit Mode */}
                  {editableCommentId === comment._id ? (
                    <Box sx={{ mt: 1, position: "relative" }}>
                      <Box sx={{ position: "relative" }}>
                        <TextField
                          value={editedComment}
                          onChange={handleEditedCommentChange}
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={5}
                          onKeyDown={handleKeyDown}
                          inputRef={inputRef}
                          placeholder={t("Edit comment... (Type @ to mention)")}
                          autoFocus
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              bgcolor: theme.palette.background.paper,
                              "& fieldset": {
                                borderColor: theme.palette.divider,
                              },
                              "&:hover fieldset": {
                                borderColor: theme.palette.text.secondary,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                                borderWidth: "2px",
                              },
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() => {
                              setEditableCommentId(null);
                              setEditedComment("");
                            }}
                            sx={{
                              color: theme.palette.text.secondary,
                              textTransform: "none",
                              fontSize: "13px",
                              px: 2,
                              "&:hover": {
                                backgroundColor: "rgba(100, 116, 139, 0.08)",
                              },
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!editedComment.trim() || isUpdating}
                            onClick={() => handleSaveEditedComment(comment._id)}
                            sx={{
                              textTransform: "none",
                              fontSize: "13px",
                              px: 2,
                              boxShadow: "none",
                              "&:hover": {
                                boxShadow: "none",
                              },
                            }}
                          >
                            {isUpdating ? (
                              <>
                                <CircularProgress size={14} sx={{ mr: 1 }} />
                                Saving...
                              </>
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </Box>
                      </Box>

                      {showEditMentionList && filteredUsers.length > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            mt: 0.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            maxHeight: 200,
                            width: "280px",
                            overflow: "auto",
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
                            zIndex: 1000,
                          }}
                        >
                          {filteredUsers.map((user, index) => (
                            <Box
                              key={user._id}
                              sx={{
                                px: 1.5,
                                py: 1,
                                cursor: "pointer",
                                bgcolor:
                                  selectedUserIndex === index
                                    ? "rgba(99, 102, 241, 0.12)"
                                    : "transparent",
                                "&:hover": {
                                  bgcolor: "rgba(99, 102, 241, 0.12)",
                                },
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                transition: "all 0.15s",
                                borderBottom:
                                  index < filteredUsers.length - 1
                                    ? "1px solid #f1f5f9"
                                    : "none",
                              }}
                              onClick={() => handleMentionSelect(user)}
                            >
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: "11px",
                                  fontWeight: 600,
                                }}
                              >
                                {user.fullName[0]}
                              </Avatar>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {user.fullName}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        mt: 0.5,
                      }}
                    >
                      {renderCommentText(comment.comment, users)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 2,
              px: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#fafbfc",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              {t("No comments yet")}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Modal */}
      <ModalBasic
        open={isDeleteCommentModal}
        setOpen={setIsDeleteCommentModal}
        title={t("Delete Comment")}
        onSave={handleDelete}
        titleSave={t("Confirm")}
        loading={isDeleting}
        styleProp={{}}
        willUnMount={() => setIsDeleteCommentModal(false)}
      >
        <DialogContent>
          {t("Are you sure you want to delete this comment?")}
        </DialogContent>
      </ModalBasic>

      <AiNotConfiguredDialog
        open={showAiConfigDialog}
        onClose={() => setShowAiConfigDialog(false)}
      />
    </Box>
  );
}
